import deleteIcon from 'Assets/icons/delete.svg';
import closeBtn from 'Assets/images/close-btn.svg';
import { observer } from 'mobx-react';
import { useUIStore } from 'Modules/ui/index.store';
import { useConfirmationModalStore } from 'Modules/ui/modals/store';
import {
  useDeleteTranscription,
  useFetchTranscriptions,
} from 'Modules/videoHistory/requests';
import { isTranscription, mergeSegments } from 'Modules/videoHistory/utils';
import React from 'react';
import {
  SidebarContent,
  SidebarHeader,
  SidebarSeparator,
} from '~/lib/ui/sidebar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/lib/ui/tabs';
import { cn } from '~/lib/utils';
import { VideoSummarySidePanelProps } from './index.types';
import { SummaryContent } from './SummaryContent';
import { TranscriptionsList } from './TranscriptionsList';

export const VideoSummarySidePanel = observer(
  ({ hidden }: VideoSummarySidePanelProps) => {
    const { videoSummarySidePanelInfo, resetVideoSummarySidePanel } =
      useUIStore();
    const { setOpenConfirmationModal } = useConfirmationModalStore();

    const conferenceId = videoSummarySidePanelInfo?.conferenceId;

    const { data: transcriptionData } = useFetchTranscriptions(conferenceId);
    const { mutate: deleteTranscription, data: isTranscriptionDeleted } =
      useDeleteTranscription(conferenceId);

    const transcriptionSegments = transcriptionData?.pages?.flatMap((page) =>
      isTranscription(page) ? page.segments : []
    );
    const firstTranscriptionPage = transcriptionData?.pages[0];
    const hasTranscriptionContent =
      !isTranscriptionDeleted && (mergeSegments(transcriptionSegments)?.length ?? 0) > 0;

    const [activeTab, setActiveTab] = React.useState<
      'summary' | 'transcription'
    >('summary');

    const handleDelete = () => {
      if (!conferenceId) {
        return;
      }

      setOpenConfirmationModal({
        title: 'Delete transcription?',
        description:
          'This will permanently delete the transcription and the meeting summary. This action cannot be undone and all associated data will be lost.',
        textConfirm: 'Delete',
        textCancel: 'Cancel',
        onConfirm: () => {
          deleteTranscription();
        },
      });
    };

    const componentTestId = 'video-summary-side-panel';

    return (
      <div
        className={cn('flex flex-col h-full', { '!hidden': hidden })}
        data-testid={componentTestId}
      >
        <SidebarHeader className="justify-center h-[78px]">
          <div className="flex justify-between items-center pl-8">
            <h4 className="text-m">Summary and transcription (Beta)</h4>
            <button
              type="button"
              aria-label="Close panel"
              className="cursor-pointer p-3.5"
              onClick={() => resetVideoSummarySidePanel()}
              data-testid={`${componentTestId}-close-button`}
            >
              <img src={closeBtn} alt="" className="w-4 h-4" />
            </button>
          </div>
        </SidebarHeader>

        <SidebarSeparator />

        <SidebarContent className="py-4.5">
          {!hidden && conferenceId && (
            <Tabs
              defaultValue="summary"
              className="gap-9 flex-1 min-h-0"
              onValueChange={(value: 'summary' | 'transcription') =>
                setActiveTab(value)
              }
              value={activeTab}
            >
              <div className="flex items-center justify-between pl-4.5 pr-8">
                <TabsList>
                  <TabsTrigger
                    value="summary"
                    data-testid={`${componentTestId}-summary-tab`}
                  >
                    Summary
                  </TabsTrigger>
                  <TabsTrigger
                    value="transcription"
                    data-testid={`${componentTestId}-transcription-tab`}
                  >
                    Transcription
                  </TabsTrigger>
                </TabsList>
                {activeTab === 'transcription' &&
                  isTranscription(firstTranscriptionPage) &&
                  firstTranscriptionPage.status === 'AVAILABLE' &&
                  hasTranscriptionContent && (
                    <img
                      src={deleteIcon}
                      className="w-7 h-7 cursor-pointer"
                      onClick={handleDelete}
                      data-testid={`${componentTestId}-delete-button`}
                    />
                  )}
              </div>

              <TabsContent value="summary" className="min-h-0">
                <SummaryContent
                  conferenceId={conferenceId}
                  fetchEnabled={activeTab === 'summary'}
                  isDeleting={isTranscriptionDeleted}
                  testId={`${componentTestId}-summary-content`}
                />
              </TabsContent>
              <TabsContent value="transcription" className="min-h-0">
                <TranscriptionsList
                  conferenceId={conferenceId}
                  fetchEnabled={activeTab === 'transcription'}
                  isDeleting={isTranscriptionDeleted}
                  testId={`${componentTestId}-transcriptions-list`}
                />
              </TabsContent>
            </Tabs>
          )}
        </SidebarContent>
      </div>
    );
  }
);
